import Request from '../Request';

export type SavedViewReportKey = 'vendor_performance' | 'top_sellers' | 'aged_inventory' | 'inventory_health' | 'plan_otb' | 'weekly_action' | 'core_replenishment';
export type SavedViewVisibility = 'only_me' | 'shared';

// Version of the payload shape each report writes; bump it when a report changes that shape.
export const SAVED_VIEW_SCHEMA_VERSION = 1;
// Server-side limits (SavedViewsController).
export const SAVED_VIEW_NAME_MAX_LENGTH = 191;
export const SAVED_VIEW_IMPORT_MAX = 200;

export interface SavedViewRecord<P = Record<string, unknown>> {
  id: number;
  reportKey: SavedViewReportKey;
  name: string;
  visibility: SavedViewVisibility;
  payload: P;
  schemaVersion: number;
  // The view's id in the browser before it was imported; only sent for the caller's own views.
  legacyId: string | null;
  ownerName: string | null;
  // Only the owner may rename, re-share or delete a view.
  canEdit: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NewSavedView<P> {
  name: string;
  visibility: SavedViewVisibility;
  payload: P;
}

export interface LegacySavedView<P> {
  legacyId: string;
  name: string;
  payload: P;
}

export interface SavedViewsImportResult<P> {
  imported: number;
  views: SavedViewRecord<P>[];
}

interface ApiSavedView {
  id: number;
  report_key: SavedViewReportKey;
  name: string;
  visibility: SavedViewVisibility;
  payload: unknown;
  schema_version?: number;
  legacy_id?: string | null;
  owner_name?: string | null;
  can_edit?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

const BASE_URI = '/api/reports/saved-views';

const toRecord = <P>(view: ApiSavedView): SavedViewRecord<P> => ({
  id: Number(view.id),
  reportKey: view.report_key,
  name: view.name,
  visibility: view.visibility === 'shared' ? 'shared' : 'only_me',
  payload: (view.payload ?? {}) as P,
  schemaVersion: view.schema_version ?? SAVED_VIEW_SCHEMA_VERSION,
  legacyId: view.legacy_id ?? null,
  ownerName: view.owner_name ?? null,
  canEdit: Boolean(view.can_edit),
  createdAt: view.created_at ?? null,
  updatedAt: view.updated_at ?? null,
});

const toRecords = <P>(views: ApiSavedView[] | undefined): SavedViewRecord<P>[] => (views ?? []).map((view) => toRecord<P>(view));

/**
 * GET /api/reports/saved-views -- the caller's own views of the report plus every view shared
 * within the client. Throws when the request fails, so callers can tell "none" from "unreachable".
 */
async function list<P>(clientId: string | number, reportKey: SavedViewReportKey): Promise<SavedViewRecord<P>[]> {
  const res = await Request.get(BASE_URI, { client_id: clientId, report_key: reportKey }, false, true);
  return toRecords<P>(res?.views);
}

async function create<P>(clientId: string | number, reportKey: SavedViewReportKey, view: NewSavedView<P>): Promise<SavedViewRecord<P>> {
  const res = await Request.post(BASE_URI, {
    client_id: clientId,
    report_key: reportKey,
    name: view.name,
    visibility: view.visibility,
    payload: view.payload,
    schema_version: SAVED_VIEW_SCHEMA_VERSION,
  });
  return toRecord<P>(res.view);
}

async function update<P>(id: number, changes: Partial<NewSavedView<P>>): Promise<SavedViewRecord<P>> {
  const res = await Request.patch(`${BASE_URI}/${id}`, changes);
  return toRecord<P>(res.view);
}

async function remove(id: number): Promise<void> {
  await Request._delete(`${BASE_URI}/${id}`);
}

/**
 * POST /api/reports/saved-views/import -- copies views kept in this browser into the account as
 * private views. Safe to repeat: views already imported (by legacy id) are skipped.
 */
async function importLegacy<P>(
  clientId: string | number,
  reportKey: SavedViewReportKey,
  views: LegacySavedView<P>[]
): Promise<SavedViewsImportResult<P>> {
  const res = await Request.post(`${BASE_URI}/import`, {
    client_id: clientId,
    report_key: reportKey,
    views: views.slice(0, SAVED_VIEW_IMPORT_MAX).map((view) => ({
      legacy_id: view.legacyId,
      name: view.name,
      payload: view.payload,
      schema_version: SAVED_VIEW_SCHEMA_VERSION,
    })),
  });
  return { imported: Number(res?.imported ?? 0), views: toRecords<P>(res?.views) };
}

const SavedViewsApi = {
  list,
  create,
  update,
  remove,
  importLegacy,
};

export default SavedViewsApi;
